/**
 * Marathon player flow: lobby → continuous mode chain → death cinematic with progress bar.
 */

import { showChallengeRevealText } from "../challenges/roulette-ui";
import {
  fetchMarathonStatus,
  postMarathonAbandon,
  postMarathonAdvance,
  postMarathonFail,
  postMarathonStart,
  type MarathonAttempt,
  type MarathonLeaderboardRow,
  type MarathonSegmentView,
} from "./api";

export type MarathonRuntimeHooks = {
  isSignedIn: () => boolean;
  openSignIn: () => void;
  showMarathonScreen: () => void;
  hideMarathonScreen: () => void;
  getMarathonMount: () => HTMLElement | null;
  startModeRun: (modeId: string, opts?: { levelIndex?: number | null }) => void | Promise<void>;
  endRunQuiet: () => void;
  restoreMenuCursor: () => void;
  returnToHub: () => void;
  getScore: () => number;
  getMarathonProgress: (modeId: string, metric: "score" | "time") => number;
  setDotsBalance: (balance: number) => void;
  addOwnedCosmetic: (id: string) => void;
  setReturnBtnLabel: (label: string) => void;
  setFadeOverlay: (opacity: number) => void;
  setMarathonRunPaused: (paused: boolean) => void;
  playModeCardBeep: () => void;
  setMarathonGoalCompleteFx: (active: boolean) => void;
  playSfx: (kind: "goal_complete" | "reveal_show" | "reveal_hide" | "reward_claim" | "fail") => void;
  persistRunBestIfAny: () => void;
  hideHudForDeath: (hide: boolean) => void;
  setChallengeDeathWorld: (opts: { frozen: boolean; ballAlpha: number }) => void;
  clearChallengeDeathWorld: () => void;
  beginChallengeDeathAsh: () => void;
  tickChallengeDeathAsh: (dt: number, t: number) => void;
  showMarathonLostScreen: (opts: {
    segments: MarathonSegmentView[];
    failSegmentIndex: number;
    failProgressRatio: number;
  }) => void;
  hideMarathonLostScreen: () => void;
  updateMarathonLostCursor: (t: number) => void;
  /** Between-leg transition: same bar/cursor style as the lose screen. */
  showMarathonAdvanceScreen: (opts: {
    segments: MarathonSegmentView[];
    fromIndex: number;
    toIndex: number;
  }) => void;
  hideMarathonAdvanceScreen: () => void;
  updateMarathonAdvanceCursor: (t: number) => void;
  showMarathonProgressHud: (opts: {
    segments: MarathonSegmentView[];
    currentIndex: number;
  }) => void;
  hideMarathonProgressHud: () => void;
  getCanvasSize: () => { w: number; h: number };
  setMouse: (x: number, y: number) => void;
};

type Phase = "idle" | "lobby" | "playing" | "advancing" | "death_seq" | "finished";
type DeathStep = "hold" | "ball_fade" | "black_in" | "lost_hold" | "black_out";

const DEATH_HOLD_MS = 2000;
const DEATH_BALL_FADE_MS = 3000;
const DEATH_BLACK_IN_MS = 3000;
const DEATH_LOST_HOLD_MS = 4500;
const DEATH_BLACK_OUT_MS = 3000;
const RETURN_SURE_MS = 2200;
const ADVANCE_FADE_IN_MS = 450;
const ADVANCE_CURSOR_MS = 1600;
const ADVANCE_HOLD_MS = 350;
const ADVANCE_FADE_OUT_MS = 450;

let hooks: MarathonRuntimeHooks | null = null;
let phase: Phase = "idle";
let segments: MarathonSegmentView[] = [];
let attempt: MarathonAttempt | null = null;
let leaderboard: MarathonLeaderboardRow[] = [];
let myBest: MarathonStatusResponseMyBest | null = null;
let busy = false;
let playElapsedAccum = 0;
let lastPlayTick = 0;
let goalCheckReadyAt = 0;
let progressBaseline = 0;
let returnArmed = false;
let returnArmUntil = 0;
let deathStep: DeathStep = "hold";
let deathStepStartedAt = 0;
let failSegmentIndex = 0;
let failProgressRatio = 0;
let lobbyView: "home" | "leaderboard" = "home";

type MarathonStatusResponseMyBest = {
  segmentsCompleted: number;
  failSegmentIndex: number;
  failProgressRatio: number;
  rankingScore: number;
  segmentCountSnapshot: number;
  rank: number | null;
};

function marathonModeIsRace(modeId: string) {
  return modeId === "speedrun" || modeId === "penalty";
}

export function mountMarathonRuntime(h: MarathonRuntimeHooks): void {
  hooks = h;
}

export function isMarathonActive(): boolean {
  return phase !== "idle";
}

export function isMarathonLobbyVisible(): boolean {
  return phase === "lobby";
}

export function isMarathonAttemptPlaying(): boolean {
  return phase === "playing";
}

export function isMarathonDeathSequence(): boolean {
  return phase === "death_seq";
}

export function isMarathonDeathWorldActive(): boolean {
  return phase === "death_seq" && (deathStep === "hold" || deathStep === "ball_fade");
}

export function isMarathonDeathShowingLost(): boolean {
  return phase === "death_seq" && (deathStep === "lost_hold" || deathStep === "black_out");
}

export function isMarathonAdvancing(): boolean {
  return phase === "advancing";
}

export function getMarathonAttempt(): MarathonAttempt | null {
  return attempt;
}

export function getMarathonRaceRemainingSec(): number | null {
  if (phase !== "playing" || !attempt || !marathonModeIsRace(attempt.modeId)) return null;
  return Math.max(0, Math.max(1, attempt.goalValue) - playElapsedAccum);
}

export function isMarathonRaceTimedOut(): boolean {
  if (phase !== "playing" || !attempt || !marathonModeIsRace(attempt.modeId)) return false;
  if (performance.now() < goalCheckReadyAt) return false;
  return playElapsedAccum >= attempt.goalValue;
}

export function marathonPauseBlocked(): boolean {
  return isMarathonActive() && phase !== "playing";
}

export function tickMarathonPlayElapsed(now: number, opts?: { paused?: boolean }) {
  if (phase !== "playing" || opts?.paused) {
    lastPlayTick = 0;
    return;
  }
  if (lastPlayTick > 0) playElapsedAccum += (now - lastPlayTick) / 1000;
  lastPlayTick = now;
}

export function checkMarathonGoalMet(): boolean {
  if (phase !== "playing" || !attempt || !hooks || busy) return false;
  if (performance.now() < goalCheckReadyAt) return false;
  // SpeedRun / Golf complete only via level-finish hooks.
  if (marathonModeIsRace(attempt.modeId)) return false;
  if (attempt.metric === "time") {
    return playElapsedAccum >= attempt.goalValue;
  }
  const progress = hooks.getMarathonProgress(attempt.modeId, attempt.metric);
  if (progress < attempt.goalValue) return false;
  // Do NOT treat a stale high baseline (leftover from the previous leg) as already clear —
  // that would auto-chain every following leg. Require real progress this segment.
  if (progressBaseline >= attempt.goalValue) return false;
  return progress > progressBaseline;
}

function renderLobby() {
  if (!hooks) return;
  const mount = hooks.getMarathonMount();
  if (!mount) return;
  mount.innerHTML = "";
  const shell = document.createElement("div");
  shell.className = "marathonScreenInner";

  const heading = document.createElement("h1");
  heading.className = "marathonHeading";
  heading.textContent = "MARATHON";

  const hint = document.createElement("p");
  hint.className = "accessHint marathonHint";
  if (!segments.length) {
    hint.textContent = "No Marathon sequence is published yet. Check back after an admin configures it.";
  } else {
    hint.textContent = `${segments.length} legs chained — clear each goal to advance. Dying ends the whole run.`;
  }

  const actions = document.createElement("div");
  actions.className = "marathonLobbyActions";

  const playBtn = document.createElement("button");
  playBtn.type = "button";
  playBtn.className = "btn green";
  playBtn.textContent = "Play Marathon";
  playBtn.disabled = !segments.length;
  playBtn.onclick = () => void beginMarathonRun();

  const lbBtn = document.createElement("button");
  lbBtn.type = "button";
  lbBtn.className = "btn";
  lbBtn.textContent = lobbyView === "leaderboard" ? "Back" : "Leaderboard";
  lbBtn.onclick = () => {
    if (lobbyView === "leaderboard") {
      lobbyView = "home";
      renderLobby();
      return;
    }
    lobbyView = "leaderboard";
    renderLobby();
    void refreshStatus()
      .then(() => {
        if (lobbyView === "leaderboard") renderLobby();
      })
      .catch(() => {});
  };

  actions.append(playBtn, lbBtn);

  const best = document.createElement("p");
  best.className = "accessHint marathonMyBest";
  if (myBest) {
    best.textContent = formatMarathonBestLine(myBest, segments.length);
  } else {
    best.textContent = "No attempts yet.";
  }

  shell.append(heading, hint, actions, best);

  if (lobbyView === "home") {
    const preview = document.createElement("div");
    preview.className = "marathonSequencePreview";
    segments.forEach((seg, i) => {
      const chip = document.createElement("div");
      chip.className = "marathonSequenceChip";
      chip.innerHTML = `<span class="marathonSequenceChipNum">${i + 1}</span><strong>${escapeHtml(seg.modeLabel)}</strong><span class="accessHint">${escapeHtml(seg.goalLabel)}</span>`;
      preview.appendChild(chip);
    });
    shell.appendChild(preview);
  } else {
    const table = document.createElement("div");
    table.className = "marathonLeaderboard";
    if (!leaderboard.length) {
      const empty = document.createElement("p");
      empty.className = "accessHint";
      empty.textContent = "No Marathon runs recorded yet.";
      table.appendChild(empty);
    } else {
      const head = document.createElement("div");
      head.className = "marathonLeaderboardRow marathonLeaderboardRow--head";
      head.innerHTML = `<span>#</span><span>Player</span><span>Progress</span>`;
      table.appendChild(head);
      for (const row of leaderboard) {
        const el = document.createElement("div");
        el.className = "marathonLeaderboardRow";
        el.innerHTML = `<span>${row.rank}</span><span>${escapeHtml(row.displayName || "Player")}</span><span>${escapeHtml(formatMarathonProgressCell(row, segments.length))}</span>`;
        table.appendChild(el);
      }
    }
    shell.appendChild(table);
  }

  mount.appendChild(shell);
}

function formatMarathonProgressCell(
  row: {
    segmentsCompleted: number;
    failProgressRatio: number;
    rankingScore: number;
    segmentCountSnapshot: number;
  },
  liveSegmentCount: number,
): string {
  const total = Math.max(1, row.segmentCountSnapshot || liveSegmentCount || 1);
  const cleared = Math.max(0, Math.floor(Number(row.segmentsCompleted) || 0));
  const pct = Math.round(Math.max(0, Math.min(1, Number(row.failProgressRatio) || 0)) * 100);
  if (cleared >= total || (Number(row.rankingScore) || 0) >= total) {
    return `Cleared ${total}/${total}`;
  }
  const leg = Math.min(total, cleared + 1);
  return `${cleared} cleared · Leg ${leg}/${total} · ${pct}%`;
}

function formatMarathonBestLine(
  best: {
    segmentsCompleted: number;
    failProgressRatio: number;
    rankingScore: number;
    segmentCountSnapshot: number;
    rank: number | null;
  },
  liveSegmentCount: number,
): string {
  const progress = formatMarathonProgressCell(best, liveSegmentCount);
  return best.rank ? `Your best: ${progress} · rank #${best.rank}` : `Your best: ${progress}`;
}

function escapeHtml(s: string) {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

async function refreshStatus() {
  const data = await fetchMarathonStatus();
  if (!data) {
    segments = [];
    leaderboard = [];
    myBest = null;
    return;
  }
  segments = Array.isArray(data.segments) ? data.segments : [];
  leaderboard = Array.isArray(data.leaderboard) ? data.leaderboard : [];
  myBest = data.myBest ?? null;
}

export async function openMarathonFlow(): Promise<void> {
  if (!hooks) return;
  if (!hooks.isSignedIn()) {
    hooks.openSignIn();
    return;
  }
  phase = "lobby";
  busy = false;
  attempt = null;
  lobbyView = "home";
  hooks.hideMarathonProgressHud();
  hooks.hideMarathonLostScreen();
  hooks.setReturnBtnLabel("Return");
  hooks.showMarathonScreen();
  hooks.playModeCardBeep();
  try {
    await refreshStatus();
  } catch {
    segments = [];
  }
  renderLobby();
}

export function closeMarathonFlow(): void {
  if (!hooks) return;
  phase = "idle";
  attempt = null;
  busy = false;
  hooks.hideMarathonProgressHud();
  hooks.hideMarathonLostScreen();
  hooks.hideMarathonScreen();
  hooks.setFadeOverlay(0);
  hooks.clearChallengeDeathWorld();
}

async function beginMarathonRun(): Promise<void> {
  if (!hooks || busy || phase !== "lobby") return;
  if (!segments.length) return;
  busy = true;
  hooks.playModeCardBeep();
  try {
    const started = await postMarathonStart();
    attempt = started.attempt;
    segments = started.segments;
  } catch (e) {
    console.warn("[marathon] start failed", e);
    busy = false;
    return;
  }
  hooks.hideMarathonScreen();
  playElapsedAccum = 0;
  lastPlayTick = 0;
  phase = "playing";
  hooks.setReturnBtnLabel("Return");
  returnArmed = false;
  hooks.setMarathonGoalCompleteFx(false);
  hooks.showMarathonProgressHud({ segments, currentIndex: attempt.segmentIndex });
  await Promise.resolve(
    hooks.startModeRun(attempt.modeId, { levelIndex: attempt.levelIndex }),
  );
  progressBaseline = hooks.getMarathonProgress(attempt.modeId, attempt.metric);
  goalCheckReadyAt = performance.now() + 1400;
  busy = false;
}

function waitMs(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function animateMs(ms: number, onTick: (t: number) => void): Promise<void> {
  return new Promise((resolve) => {
    const start = performance.now();
    const step = (now: number) => {
      const t = Math.min(1, (now - start) / Math.max(1, ms));
      onTick(t);
      if (t >= 1) {
        resolve();
        return;
      }
      requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  });
}

function attemptFromSegment(
  seg: MarathonSegmentView,
  opts: { token: string; segmentIndex: number; sequenceIds: string[] },
): MarathonAttempt {
  return {
    token: opts.token,
    segmentIndex: opts.segmentIndex,
    segmentId: seg.id,
    modeId: seg.modeId,
    metric: seg.metric,
    goalValue: Math.max(1, Math.floor(seg.goalValue)),
    modeLabel: seg.modeLabel || seg.modeId,
    levelIndex: seg.levelIndex ?? null,
    sequenceIds: opts.sequenceIds,
  };
}

function localNextAttempt(cleared: MarathonAttempt): MarathonAttempt | null {
  const nextIndex = cleared.segmentIndex + 1;
  if (nextIndex >= segments.length) return null;
  const seg = segments[nextIndex];
  if (!seg?.id || !seg.modeId) return null;
  return attemptFromSegment(seg, {
    token: cleared.token,
    segmentIndex: nextIndex,
    sequenceIds: cleared.sequenceIds.length ? cleared.sequenceIds : segments.map((s) => s.id),
  });
}

async function startCurrentSegment(): Promise<void> {
  if (!hooks || !attempt) return;
  playElapsedAccum = 0;
  lastPlayTick = 0;
  busy = true;
  hooks.setMarathonRunPaused(true);
  hooks.setFadeOverlay(1);
  hooks.setMarathonGoalCompleteFx(false);
  // Clear prior mode leftovers (SpeedRun complete state, score, death flags) before the next leg.
  hooks.endRunQuiet();
  hooks.showMarathonProgressHud({ segments, currentIndex: attempt.segmentIndex });
  await Promise.resolve(
    hooks.startModeRun(attempt.modeId, { levelIndex: attempt.levelIndex }),
  );
  // Resample after the mode starter has reset score/state.
  progressBaseline = hooks.getMarathonProgress(attempt.modeId, attempt.metric);
  goalCheckReadyAt = performance.now() + 1400;
  await animateMs(ADVANCE_FADE_OUT_MS, (t) => hooks?.setFadeOverlay(1 - t));
  hooks.setFadeOverlay(0);
  // Baseline again in case score settled during the fade.
  progressBaseline = hooks.getMarathonProgress(attempt.modeId, attempt.metric);
  goalCheckReadyAt = performance.now() + 600;
  hooks.setMarathonRunPaused(false);
  busy = false;
}

async function playAdvanceBar(fromIndex: number, toIndex: number): Promise<void> {
  if (!hooks) return;
  hooks.playSfx("reveal_show");
  hooks.showMarathonAdvanceScreen({
    segments,
    fromIndex,
    toIndex,
  });
  await animateMs(ADVANCE_CURSOR_MS, (t) => hooks?.updateMarathonAdvanceCursor(t));
  hooks.updateMarathonAdvanceCursor(1);
  await waitMs(ADVANCE_HOLD_MS);
  hooks.hideMarathonAdvanceScreen();
  hooks.playSfx("reveal_hide");
}

async function finishMarathonRun(clearedIndex: number): Promise<void> {
  if (!hooks) return;
  await playAdvanceBar(clearedIndex, Math.max(clearedIndex, segments.length - 1));
  hooks.endRunQuiet();
  attempt = null;
  hooks.hideMarathonProgressHud();
  hooks.restoreMenuCursor();
  await animateMs(ADVANCE_FADE_OUT_MS, (t) => hooks?.setFadeOverlay(1 - t));
  hooks.setFadeOverlay(0);
  phase = "lobby";
  busy = false;
  hooks.setReturnBtnLabel("Return");
  hooks.showMarathonScreen();
  await refreshStatus().catch(() => {});
  renderLobby();
  hooks.playSfx("reveal_show");
  await showChallengeRevealText(["MARATHON COMPLETE"], 2200);
  hooks.playSfx("reveal_hide");
}

/** Called when the current leg goal is met — animate progress, then start the next leg. */
export async function onMarathonGoalReached(): Promise<void> {
  if (!hooks || phase !== "playing" || !attempt || busy) return;
  busy = true;
  phase = "advancing";
  // Marathon uses its own progress-bar transition — never the Challenges blue edge FX.
  hooks.setMarathonGoalCompleteFx(false);
  hooks.persistRunBestIfAny();
  hooks.setMarathonRunPaused(true);
  hooks.hideMarathonProgressHud();

  const cleared = attempt;
  const clearedIndex = cleared.segmentIndex;
  const token = cleared.token;
  const modeId = cleared.modeId;
  const metric = cleared.metric;
  const goalValue = cleared.goalValue;
  let score = hooks.getMarathonProgress(modeId, metric);
  let timeSec = playElapsedAccum;
  if (marathonModeIsRace(modeId)) {
    score = Math.max(score, 1);
    // Finish hook already decided the clear — don't let a drifted marathon clock fail the API.
    timeSec = Math.min(timeSec, goalValue);
  } else if (metric === "score") {
    score = Math.max(score, goalValue);
  } else {
    timeSec = Math.max(timeSec, goalValue);
  }

  // Start the veil immediately so the player sees the transition, not a frozen goal FX.
  await animateMs(ADVANCE_FADE_IN_MS, (t) => hooks?.setFadeOverlay(t));
  hooks.setFadeOverlay(1);

  let res: Record<string, unknown> | null = null;
  try {
    res = await postMarathonAdvance(token, { score, timeSec });
  } catch (e) {
    console.warn("[marathon] advance failed, retrying", e);
    try {
      // Second chance with fully padded progress (covers race clock drift / score floors).
      const retryScore = marathonModeIsRace(modeId) ? 1 : metric === "score" ? goalValue : score;
      const retryTime = marathonModeIsRace(modeId) || metric === "time" ? goalValue : timeSec;
      res = await postMarathonAdvance(token, { score: retryScore, timeSec: retryTime });
    } catch (e2) {
      console.warn("[marathon] advance retry failed", e2);
    }
  }

  try {
    if (res) {
      if (typeof (res.dots as { balance?: number } | undefined)?.balance === "number") {
        hooks.setDotsBalance((res.dots as { balance: number }).balance);
      }
      const cosmetic = res.rewardCosmetic as { id?: string } | null;
      if (cosmetic?.id) hooks.addOwnedCosmetic(cosmetic.id);

      if (res.rewardGranted && res.rewardLabel) {
        hooks.playSfx("reveal_show");
        await showChallengeRevealText([String(res.rewardLabel)], 1400);
        hooks.playSfx("reward_claim");
        hooks.playSfx("reveal_hide");
      }

      if (res.finishedAll) {
        await finishMarathonRun(clearedIndex);
        return;
      }

      const next = res.attempt as MarathonAttempt | null;
      if (next?.modeId && typeof next.segmentIndex === "number") {
        await playAdvanceBar(clearedIndex, next.segmentIndex);
        attempt = next;
        phase = "playing";
        await startCurrentSegment();
        return;
      }
    }

    // API missing/failed but local sequence still has a next leg — keep the run going.
    const localNext = localNextAttempt(cleared);
    if (localNext) {
      console.warn("[marathon] continuing locally to leg", localNext.segmentIndex + 1);
      await playAdvanceBar(clearedIndex, localNext.segmentIndex);
      attempt = localNext;
      phase = "playing";
      await startCurrentSegment();
      return;
    }

    // No next leg locally either — treat as complete.
    await finishMarathonRun(clearedIndex);
  } catch (e) {
    console.warn("[marathon] advance presentation failed", e);
    hooks.hideMarathonAdvanceScreen();
    hooks.setMarathonGoalCompleteFx(false);
    const localNext = localNextAttempt(cleared);
    if (localNext) {
      attempt = localNext;
      phase = "playing";
      try {
        await startCurrentSegment();
        return;
      } catch (startErr) {
        console.warn("[marathon] local next-leg start failed", startErr);
      }
    }
    attempt = null;
    hooks.endRunQuiet();
    hooks.restoreMenuCursor();
    await animateMs(ADVANCE_FADE_OUT_MS, (t) => hooks?.setFadeOverlay(1 - t));
    hooks.setFadeOverlay(0);
    phase = "lobby";
    busy = false;
    hooks.setReturnBtnLabel("Return");
    hooks.showMarathonScreen();
    await refreshStatus().catch(() => {});
    renderLobby();
  }
}

export async function onMarathonDeathFail(): Promise<void> {
  if (!hooks || phase !== "playing" || busy) return;
  busy = true;
  phase = "death_seq";
  deathStep = "hold";
  deathStepStartedAt = performance.now();
  returnArmed = false;
  hooks.setReturnBtnLabel("Return");
  hooks.setMarathonGoalCompleteFx(false);
  hooks.playSfx("fail");
  hooks.hideHudForDeath(true);
  hooks.hideMarathonLostScreen();
  hooks.setFadeOverlay(0);
  hooks.setChallengeDeathWorld({ frozen: true, ballAlpha: 1 });

  const token = attempt?.token || "";
  failSegmentIndex = attempt?.segmentIndex ?? 0;
  const score = attempt ? hooks.getMarathonProgress(attempt.modeId, attempt.metric) : 0;
  const timeSec = playElapsedAccum;
  if (attempt) {
    const goal = Math.max(1, attempt.goalValue);
    if (marathonModeIsRace(attempt.modeId)) {
      failProgressRatio = score >= 1 ? 1 : Math.max(0, Math.min(0.999, timeSec / goal));
    } else if (attempt.metric === "time") {
      failProgressRatio = Math.max(0, Math.min(1, timeSec / goal));
    } else {
      failProgressRatio = Math.max(0, Math.min(1, score / goal));
    }
  } else {
    failProgressRatio = 0;
  }

  const failPayload = {
    score,
    timeSec,
    segmentIndex: failSegmentIndex,
    failSegmentIndex,
    segmentsCompleted: failSegmentIndex,
    failProgressRatio,
  };
  attempt = null;
  try {
    await postMarathonFail(token, failPayload);
  } catch {
    /* keep cinematic */
  }
}

export function tickMarathonDeathSequence(now: number, dt = 1 / 60): boolean {
  if (!hooks || phase !== "death_seq") return false;
  const elapsed = now - deathStepStartedAt;

  if (deathStep === "hold") {
    hooks.setChallengeDeathWorld({ frozen: true, ballAlpha: 1 });
    if (elapsed >= DEATH_HOLD_MS) {
      deathStep = "ball_fade";
      deathStepStartedAt = now;
      hooks.beginChallengeDeathAsh();
    }
    return true;
  }

  if (deathStep === "ball_fade") {
    const t = Math.min(1, elapsed / DEATH_BALL_FADE_MS);
    hooks.setChallengeDeathWorld({ frozen: true, ballAlpha: 1 - t });
    hooks.tickChallengeDeathAsh(dt, t);
    if (t >= 1) {
      hooks.clearChallengeDeathWorld();
      hooks.setChallengeDeathWorld({ frozen: true, ballAlpha: 0 });
      deathStep = "black_in";
      deathStepStartedAt = now;
    }
    return true;
  }

  if (deathStep === "black_in") {
    const t = Math.min(1, elapsed / DEATH_BLACK_IN_MS);
    hooks.setFadeOverlay(t);
    if (t >= 1) {
      hooks.setFadeOverlay(1);
      hooks.setChallengeDeathWorld({ frozen: false, ballAlpha: 1 });
      hooks.hideHudForDeath(true);
      hooks.hideMarathonProgressHud();
      hooks.endRunQuiet();
      const { w, h } = hooks.getCanvasSize();
      hooks.setMouse(w / 2, h * 0.78);
      hooks.showMarathonLostScreen({
        segments,
        failSegmentIndex,
        failProgressRatio,
      });
      deathStep = "lost_hold";
      deathStepStartedAt = now;
    }
    return true;
  }

  if (deathStep === "lost_hold") {
    hooks.setFadeOverlay(1);
    const t = Math.min(1, elapsed / Math.max(1, DEATH_LOST_HOLD_MS * 0.55));
    hooks.updateMarathonLostCursor(t);
    if (elapsed >= DEATH_LOST_HOLD_MS) {
      deathStep = "black_out";
      deathStepStartedAt = now;
    }
    return true;
  }

  if (deathStep === "black_out") {
    const t = Math.min(1, elapsed / DEATH_BLACK_OUT_MS);
    hooks.updateMarathonLostCursor(1);
    hooks.setFadeOverlay(1 - t);
    if (t >= 1) {
      hooks.setFadeOverlay(0);
      hooks.hideMarathonLostScreen();
      hooks.clearChallengeDeathWorld();
      phase = "lobby";
      deathStep = "hold";
      deathStepStartedAt = 0;
      busy = false;
      hooks.restoreMenuCursor();
      hooks.setReturnBtnLabel("Return");
      hooks.showMarathonScreen();
      void refreshStatus()
        .then(() => renderLobby())
        .catch(() => renderLobby());
      return false;
    }
    return true;
  }

  return false;
}

export function handleMarathonReturn(): boolean {
  if (!isMarathonActive() || !hooks) return false;
  if (phase === "death_seq" || phase === "advancing") return true;
  if (phase === "lobby" || phase === "finished") {
    closeMarathonFlow();
    hooks.returnToHub();
    return true;
  }
  if (phase === "playing") {
    const now = performance.now();
    if (!returnArmed || now > returnArmUntil) {
      returnArmed = true;
      returnArmUntil = now + RETURN_SURE_MS;
      hooks.setReturnBtnLabel("Sure?");
      return true;
    }
    returnArmed = false;
    hooks.setReturnBtnLabel("Return");
    void (async () => {
      try {
        await postMarathonAbandon();
      } catch {
        /* ignore */
      }
      attempt = null;
      hooks?.hideMarathonProgressHud();
      hooks?.endRunQuiet();
      hooks?.restoreMenuCursor();
      hooks?.setFadeOverlay(0);
      phase = "lobby";
      busy = false;
      hooks?.showMarathonScreen();
      await refreshStatus().catch(() => {});
      renderLobby();
    })();
    return true;
  }
  return false;
}

export function tickMarathonReturnSure(now: number) {
  if (!returnArmed || !hooks) return;
  if (now > returnArmUntil) {
    returnArmed = false;
    hooks.setReturnBtnLabel("Return");
  }
}
